import { useEffect, useRef, useState } from "react";
import { ChevronDown, FileSpreadsheet, FileText } from "lucide-react";
import { css, cx } from "styled-system/css";
import { useAuth } from "@/providers/AuthProvider";
import { useToast } from "@/hooks/useToast";
import { fetchWithSessionSync } from "@/lib/fetch-with-session";

type ExportFormat = "csv" | "xlsx";

function buildExportUrl(professionalQuery: string, format: ExportFormat): string {
  const sep = professionalQuery ? "&" : "?";
  return `/api/dashboard/clients/export${professionalQuery}${sep}format=${format}`;
}

/** Botón "Exportar" con dropdown de formato (CSV / Excel) para la pantalla de Clientes. */
export function ExportClientsButton({ professionalQuery }: { professionalQuery: string }) {
  const { refresh } = useAuth();
  const toast = useToast();
  const [open, setOpen] = useState(false);
  const [loadingFormat, setLoadingFormat] = useState<ExportFormat | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: PointerEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  async function handleExport(format: ExportFormat) {
    setOpen(false);
    setLoadingFormat(format);
    try {
      const r = await fetchWithSessionSync(
        buildExportUrl(professionalQuery, format),
        { credentials: "include" },
        refresh,
      );
      if (!r.ok) throw new Error("export failed");
      const blob = await r.blob();
      const dateStr = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `pacientes-${dateStr}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo exportar los pacientes. Probá de nuevo.");
    } finally {
      setLoadingFormat(null);
    }
  }

  const isLoading = loadingFormat !== null;

  return (
    <div ref={rootRef} className={css({ position: "relative", display: "inline-block" })}>
      <button
        type="button"
        disabled={isLoading}
        onClick={() => setOpen((v) => !v)}
        className={css({
          display: "inline-flex",
          alignItems: "center",
          gap: "1.5",
          px: "4",
          py: "2.5",
          rounded: "lg",
          fontSize: "sm",
          fontWeight: "semibold",
          cursor: "pointer",
          bg: "white",
          color: "#334155",
          borderWidth: "1px",
          borderColor: "#e2e8f0",
          transition: "transform 160ms cubic-bezier(0.23, 1, 0.32, 1), opacity 150ms ease, background 150ms ease",
          _hover: { bg: "#f8fafc" },
          _active: { transform: "scale(0.97)" },
          _disabled: { opacity: 0.6, cursor: "not-allowed" },
        })}
      >
        {isLoading ? "Exportando…" : "Exportar"}
        {!isLoading && (
          <ChevronDown
            size={14}
            strokeWidth={2.5}
            className={css({
              transition: "transform 150ms ease",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            })}
          />
        )}
      </button>

      {open && (
        <div
          role="menu"
          className={cx(
            css({
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              minW: "220px",
              bg: "white",
              rounded: "lg",
              borderWidth: "1px",
              borderColor: "#e2e8f0",
              boxShadow: "0 12px 32px rgba(15,23,42,0.14)",
              overflow: "hidden",
              zIndex: 30,
              transformOrigin: "top right",
            }),
          )}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => void handleExport("csv")}
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "2.5",
              w: "full",
              px: "3.5",
              py: "2.5",
              fontSize: "sm",
              color: "#334155",
              bg: "transparent",
              border: "none",
              textAlign: "left",
              cursor: "pointer",
              _hover: { bg: "#f8fafc" },
            })}
          >
            <FileText size={15} strokeWidth={2} className={css({ color: "#64748b", flexShrink: 0 })} />
            Descargar CSV
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => void handleExport("xlsx")}
            className={css({
              display: "flex",
              alignItems: "center",
              gap: "2.5",
              w: "full",
              px: "3.5",
              py: "2.5",
              fontSize: "sm",
              color: "#334155",
              bg: "transparent",
              border: "none",
              textAlign: "left",
              cursor: "pointer",
              borderTopWidth: "1px",
              borderColor: "#f1f5f9",
              _hover: { bg: "#f8fafc" },
            })}
          >
            <FileSpreadsheet size={15} strokeWidth={2} className={css({ color: "#64748b", flexShrink: 0 })} />
            Descargar Excel (.xlsx)
          </button>
        </div>
      )}
    </div>
  );
}
